import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * Known upsell trigger points. Every prompt in the platform is one of
 * these, keyed by ID so we can add analytics later and see which prompt
 * converts. Copy lives on the prompt itself so sections don't rewrite it.
 */
export type UpsellTriggerId =
  | 'workflow-limit-free'
  | 'invite-limit-free'
  | 'seat-limit-team'
  | 'storage-limit-free'
  | 'invocations-limit-free';

export interface UpsellPrompt {
  id: UpsellTriggerId;
  title: string;
  body: string;
  primaryCta: { label: string; href: string };
  dismissLabel: string;
}

/**
 * Canonical library of the three (five, once we grow) upsell prompts. Copy
 * matches the pricing page. Keep messages short — a full sentence, one
 * action button, and a way out. Longer copy converts worse.
 */
export const UPSELL_PROMPTS: Record<UpsellTriggerId, UpsellPrompt> = {
  'workflow-limit-free': {
    id: 'workflow-limit-free',
    title: 'Nice — you built 3 workflows.',
    body: 'The Free plan tops out at 3 workflows. Team unlocks unlimited workflows, storage, and invocations for $30 per user per month.',
    primaryCta: { label: 'Upgrade to Team', href: '/signup?deploy=cloud&plan=team' },
    dismissLabel: 'Later',
  },
  'invite-limit-free': {
    id: 'invite-limit-free',
    title: 'Adding teammates?',
    body: 'Free plan is single-user. Upgrade to Team to invite the rest of your team — $30 per user per month, up to 25 seats before you talk to us.',
    primaryCta: { label: 'Upgrade to Team', href: '/signup?deploy=cloud&plan=team' },
    dismissLabel: 'Not yet',
  },
  'seat-limit-team': {
    id: 'seat-limit-team',
    title: "You've grown out of Team.",
    body: 'Team caps at 25 seats. Enterprise unlocks SSO, SCIM, a SOC 2 report, and a dedicated engineer. We usually get back within 24 hours.',
    primaryCta: { label: 'Talk to us', href: '/signup?deploy=cloud&plan=enterprise' },
    dismissLabel: 'Later',
  },
  'storage-limit-free': {
    id: 'storage-limit-free',
    title: 'You hit the 1 GB storage limit.',
    body: 'Team unlocks unlimited storage for $30 per user per month.',
    primaryCta: { label: 'Upgrade to Team', href: '/signup?deploy=cloud&plan=team' },
    dismissLabel: 'Later',
  },
  'invocations-limit-free': {
    id: 'invocations-limit-free',
    title: 'You hit the 1,000-invocation monthly cap.',
    body: 'Team unlocks unlimited invocations for $30 per user per month. New invocations resume immediately after upgrade.',
    primaryCta: { label: 'Upgrade to Team', href: '/signup?deploy=cloud&plan=team' },
    dismissLabel: 'Later',
  },
};

interface UpsellContextValue {
  prompt: (id: UpsellTriggerId) => void;
  dismiss: () => void;
  active: UpsellPrompt | null;
}

const UpsellContext = createContext<UpsellContextValue | null>(null);

/**
 * Provider wraps the whole authenticated shell. Sections call
 * useUpsell().prompt('workflow-limit-free') to fire a modal at the moment
 * a user hits a Free-plan cap. The modal is a single component here so
 * copy stays consistent and analytics can hook in at one point later.
 */
export function UpsellProvider({ children }: { children: ReactNode }): ReactNode {
  const [activeId, setActiveId] = useState<UpsellTriggerId | null>(null);

  const prompt = useCallback((id: UpsellTriggerId) => {
    setActiveId(id);
  }, []);

  const dismiss = useCallback(() => {
    setActiveId(null);
  }, []);

  const active = activeId ? UPSELL_PROMPTS[activeId] : null;

  const value = useMemo<UpsellContextValue>(
    () => ({ prompt, dismiss, active }),
    [prompt, dismiss, active],
  );

  return (
    <UpsellContext.Provider value={value}>
      {children}
      <UpsellModal prompt={active} onDismiss={dismiss} />
    </UpsellContext.Provider>
  );
}

export function useUpsell(): UpsellContextValue {
  const ctx = useContext(UpsellContext);
  if (!ctx) throw new Error('useUpsell must be used inside <UpsellProvider>');
  return ctx;
}

function UpsellModal({
  prompt,
  onDismiss,
}: {
  prompt: UpsellPrompt | null;
  onDismiss: () => void;
}): ReactNode {
  if (!prompt) return null;
  return (
    <Modal
      visible={prompt !== null}
      onDismiss={onDismiss}
      header={prompt.title}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              {prompt.dismissLabel}
            </Button>
            <a href={prompt.primaryCta.href} style={{ textDecoration: 'none' }}>
              <Button variant="primary">{prompt.primaryCta.label}</Button>
            </a>
          </SpaceBetween>
        </Box>
      }
    >
      <Alert type="info">{prompt.body}</Alert>
    </Modal>
  );
}
